import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Button, Box, TextField, InputAdornment, IconButton, MenuItem } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

import AppInfoView from "@crema/core/AppInfoView";
import AppsContainer from "@crema/core/AppsContainer";
import AppsPagination from "@crema/core/AppsPagination";
import AppsContent from "@crema/core/AppsContainer/AppsContent";
import AppsHeader from "@crema/core/AppsContainer/AppsHeader";

import { useNavigate } from "react-router-dom";
import CmsTable from "./CmsPageTable";
import { getCms } from "redux/actions/cmsMaster";
import { AppState } from "redux/store";
import { isModuleAuthorized } from "@crema/utility/Utils";

const Cms = () => {
  const { messages } = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { cms } = useSelector<AppState, AppState["cmsMaster"]>(({ cmsMaster }) => cmsMaster);

  const rowsPerPage = 10;
  const [search, setSearchQuery] = useState<string>("");
  const [searchField, setSearchField] = useState("all");
  const [page, setPage] = useState(0);

  const searchFields = [
    { label: "All", value: "all" },
    { label: "Page URL", value: "PageTitle" },
    { label: "Meta Title", value: "MetaTitle" },
    { label: "Meta Keyword", value: "MetaKeyword" },
    { label: "Meta Description", value: "MetaDesc" },
  ];

  useEffect(() => {
    dispatch(getCms("", 0, 10000)); 
  }, [dispatch]);

  const handleAddCmsClick = () => {
    navigate("/pages/CmsPageCreate");
  };

  const onSearchCms = (value: string) => {
    setSearchQuery(value);
    setPage(0);
  };

  const onPageChange = (event: React.ChangeEvent<unknown> | null, value: number) => {
    setPage(value);
  };

  const filteredCms = search.trim()
    ? cms.filter((item) => {
        const term = search.toLowerCase();
        if (searchField === "all") {
          return searchFields
            .filter((f) => f.value !== "all")
            .some(({ value }) => item[value]?.toLowerCase().includes(term));
        } else {
          return item[searchField]?.toLowerCase().includes(term);
        }
      })
    : cms;

  const paginatedCms = filteredCms.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  return (
    <>
      <AppsContainer
        title={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: 16,
                fontWeight: "bold",
                color: "text.primary",
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {messages["sidebar.cmsMaster"]}
            </Box>
            {isModuleAuthorized("Add CMS") && (
              <Button
                variant="outlined"
                color="primary"
                onClick={handleAddCmsClick}
                sx={{ flexShrink: 0 }}
              >
                Add CMS
              </Button>
            )}
          </Box>
        }
        fullView
      >
        <AppsHeader>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", justifyContent: "flex-end", width: 1 }}>
            <TextField
              select
              size="small"
              value={searchField}
              onChange={(e) => {
                setSearchField(e.target.value);
                setSearchQuery("");
                setPage(0);
              }}
              sx={{ width: 160 }}
            >
              {searchFields.map((field) => (
                <MenuItem key={field.value} value={field.value}>
                  {field.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => onSearchCms(e.target.value)}
              placeholder={messages["common.searchHere"] as string}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {search ? (
                      <IconButton onClick={() => onSearchCms("")} size="small">
                        <CloseIcon />
                      </IconButton>
                    ) : (
                      <SearchIcon />
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{ width: 200 }}
            />
          </Box>
        </AppsHeader>

        <AppsContent
          sx={{
            paddingTop: 2.5,
            paddingBottom: 2.5,
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
          }}
        >
          <CmsTable cms={paginatedCms} />
          <AppsPagination
            rowsPerPage={rowsPerPage}
            count={filteredCms.length}
            page={page}
            onPageChange={onPageChange}
          />
        </AppsContent>
      </AppsContainer>

      <AppInfoView />
    </>
  );
};

export default Cms;
